import { TableUtils, fieldTypes } from './TableUtils'
import { formatDateForDisplay, isValidDate } from '../Utility'
import { CURRENCYFORMAT } from '../../../config/Constants'
import { isValid } from 'date-fns'
import Flatpickr from 'react-flatpickr'
import { Italian } from 'flatpickr/dist/l10n/it'
import { english } from 'flatpickr/dist/l10n/default'

// TABLE
import { Cell } from '@table-library/react-table-library'

// COMPONENTS
import NumberFormat from 'react-number-format'
import { BwmSelect } from '../form/BwmSelect'
import { memo } from 'react'

function TableInputField({
  key,
  type = fieldTypes.TEXT,
  value = '',
  onChange = (value) => {},
  options = [{ id: null, description: '' }],
  lang = 'it'
} = {}) {
  if ([fieldTypes.TEXT].includes(type)) {
    return (
      <input
        key={key}
        type={type}
        onChange={(e) => onChange(e.target.value)}
        value={value}
        className={'form-control form-control-cell'}
      />
    )
  } else if (fieldTypes.DATE === type) {
    return (
      <Flatpickr
        className={`form-control form-control-date form-control-cell`}
        placeholder={lang === 'it' ? 'gg/mm/aaaa' : 'dd/mm/yyyy'}
        options={{
          maxDate: '2500-01-01',
          allowInput: true,
          dateFormat: 'd/m/Y',
          language: lang === 'it' ? Italian : english,
          locale: lang
        }}
        value={value !== null && isValid(new Date(value)) ? value : ''}
        onChange={([date]) => onChange(isValidDate(new Date(date)) ? new Date(date) : undefined)}
      />
    )
  } else if ([fieldTypes.NUMBER, fieldTypes.CURRENCY].includes(type)) {
    return (
      <NumberFormat
        key={key}
        thousandSeparator={'.'}
        decimalSeparator={','}
        decimalScale={0}
        fixedDecimalScale={true}
        className={'form-control form-control-cell'}
        inputMode="numeric"
        value={value}
        onValueChange={(value) => onChange(value.floatValue)}
      />
    )
  } else if (fieldTypes.SELECTION === type) {
    return (
      <BwmSelect
        isCell={true}
        isSearchable={false}
        isClearable={false}
        optionValue="id"
        optionLabel="description"
        value={value}
        options={options}
        onChange={(value) => onChange(value?.id)}
      />
    )
  }

  return <>{value}</>
}

function formatCellValue({ type, fieldValue } = {}) {
  if ([fieldTypes.NUMBER, fieldTypes.CURRENCY].includes(type)) {
    return fieldValue ? CURRENCYFORMAT.format(fieldValue) : ''
  }
  if (fieldTypes.DATE === type) {
    return formatDateForDisplay(fieldValue)
  } else {
    return fieldValue || ''
  }
}

function TableCell({ rowId, cellData, key, hide, type, pinRight, pinLeft, idToCompare } = {}) {
  const textValue = formatCellValue({ type: type, fieldValue: cellData?.fieldValue })
  const component = cellData?.component()
  if (hide) {
    return <Cell hide={true} />
  } else if (component && textValue) {
    return (
      <Cell
        key={key}
        title={textValue}
        hide={hide}
        className={`table-row ${cellData.additionalClass} ${rowId === idToCompare ? 'bg-primary-rgb' : ''}`}
        pinRight={pinRight}
        pinLeft={pinLeft}>
        {component}
      </Cell>
    )
  } else if (component) {
    return (
      <Cell
        key={key}
        title={textValue}
        hide={hide}
        className={`table-row ${cellData.additionalClass} ${rowId === idToCompare ? 'bg-primary-rgb' : ''}`}
        pinRight={pinRight}
        pinLeft={pinLeft}>
        {component}
      </Cell>
    )
  } else if (textValue) {
    return (
      <Cell
        key={key}
        title={textValue}
        hide={hide}
        className={`
          ${[fieldTypes.NUMBER, fieldTypes.CURRENCY].includes(type) ? 'text-end' : ''} 
          table-row 
          ${cellData.additionalClass} 
          ${rowId === idToCompare ? 'bg-primary-rgb' : ''}
        `}
        onClick={cellData.onClick}
        pinRight={pinRight}
        pinLeft={pinLeft}>
        {textValue}
      </Cell>
    )
  } else {
    return (
      <Cell
        key={key}
        title={textValue}
        hide={hide}
        className={`table-row ${cellData.additionalClass} ${rowId === idToCompare ? 'bg-primary-rgb' : ''}`}
        onClick={cellData.onClick}
        pinRight={pinRight}
        pinLeft={pinLeft}
      />
    )
  }
}

export const StarCell = memo(function StarCell({
  rowId,
  cellData,
  index,
  header,
  hide,
  idToCompare,
  lang
}) {
  const key = `cell-${rowId}-${index}`
  const type = header.type
  const pinRight = header.pinRight
  const pinLeft = header.pinLeft

  if (cellData?.editable) {
    return (
      <Cell
        key={key}
        hide={hide}
        className={`table-row ${cellData.additionalClass} ${rowId === idToCompare ? 'bg-primary-rgb' : ''}`}
        onClick={cellData.onClick}
        pinRight={pinRight}
        pinLeft={pinLeft}>
        {TableInputField({
          key: key + '-input-field',
          value: cellData.fieldValue,
          type: type,
          onChange: (inputValue) => cellData.onEdit(rowId, cellData.fieldName, inputValue),
          options: header.options,
          lang: lang
        })}
      </Cell>
    )
  } else {
    return TableCell({
      rowId,
      cellData,
      key,
      hide: hide,
      type: type,
      header,
      pinRight,
      pinLeft,
      idToCompare
    })
  }
})
